import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";
import { cn } from "tailwind-variants";

import { SlideThumbnail } from "@/components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { layoutSettings } from "@/components/Product/ImageViewerFullWidth/imageViewerFullWidth.config";
import BreakpointsService, {
    BreakpointMaxSizes
} from "@/services/breakpoint.service";
import { useIsBreakpoint } from "@/utils/breakpoints/breakpoints.provider";
import { stopVideo } from "@/utils/imageViewer";
import { getEffectiveLayoutSettings } from "@/utils/swiper";
import "./../../../styles/scss/styleguide/vendor-overrides/innerImageZoom/_custom.scss";
import "./imageViewerFullWidth.scss";
import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";

type Props = {
    className?: string;
    images: Array<ImageViewerImage>;
    videos: Array<ImageViewerVideo>;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    defaultVideoThumbnailPath?: string;
};

export const ImageViewerFullWidthComponent = ({
    className,
    images,
    videos,
    activeIndex,
    setActiveIndex,
    defaultVideoThumbnailPath
}: Props) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const { matches: isMedium } = useIsBreakpoint("md");

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const amount = images.concat(videos).length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo(true);
        };

        window.addEventListener("hashchange", hashChangeHandler);

        return () => {
            window.removeEventListener("hashchange", hashChangeHandler);
        };
    }, []);

    useEffect(() => {
        if (mainSwiper && thumbSwiper) {
            if (mainSwiper.activeIndex !== activeIndex) {
                mainSwiper.slideTo(activeIndex);
                thumbSwiper.update();
            }
        }
    }, [activeIndex, mainSwiper, thumbSwiper]);

    return (
        <div className={cn("imageViewerFullWidth-root", className)}>
            <div className="col-xl-10 p-0 pr-xl-4 imageViewerFullWidth-centered">
                <div className={"d-flex align-items-center mw-100"}>
                    {amount > 1 && (
                        <SwiperNavButton
                            className="d-none d-md-block"
                            innerRef={navigationPrevRef}
                            disabled={activeIndex === 0}
                            icon="left"
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        onSwiper={setMainSwiper}
                        initialSlide={activeIndex}
                        onActiveIndexChange={(swiper) => {
                            setActiveIndex(swiper.activeIndex);
                            stopVideo(true);
                        }}
                        thumbs={{ swiper: thumbSwiper }}
                        allowTouchMove
                        zoom={false}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current
                        }}
                        slidesPerView={"auto"}
                        spaceBetween={5}
                    >
                        {images
                            ?.filter((img) => {
                                const src = !isMedium ? img.main : img.zoom;
                                return !!src;
                            })
                            .map((image, index) => (
                                <SwiperSlide
                                    className="imageViewerFullWidth__gallery-slide"
                                    key={`imageViewerFullWidth-main${index}`}
                                >
                                    <InnerImageZoom
                                        src={
                                            !isMedium
                                                ? image.main!
                                                : image.zoom!
                                        }
                                        className="imageViewerFullWidth__image"
                                        zoomSrc={
                                            !isMedium
                                                ? image.zoom
                                                : image.maxZoom
                                        }
                                        imgAttributes={{
                                            alt: image.altText || ""
                                        }}
                                        zoomPreload={true}
                                        fullscreenOnMobile={true}
                                        hasSpacer={true}
                                        mobileBreakpoint={BreakpointsService.getBreakpointMaxValues(
                                            BreakpointMaxSizes.md
                                        )}
                                    />
                                </SwiperSlide>
                            ))}
                        {videos?.map((video, index) => (
                            <SwiperSlide
                                key={`imageViewerFullWidth-main-video${index}`}
                                className="imageViewerFullWidth__gallery-slide
                                            imageViewerFullWidth-mediaSlide
                                            embed-responsive embed-responsive-16by9"
                            >
                                <iframe
                                    className="d-block w-100 h-100 product-video--full-width"
                                    id={`imageViewerFullWidthIframe-${index}`}
                                    src={`${video.main}`}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            className="d-none d-md-block"
                            innerRef={navigationNextRef}
                            disabled={activeIndex + 1 === amount}
                            icon="right"
                        />
                    )}
                </div>
            </div>

            <div className="col-xl-2 p-0 imageViewerFullWidth-thumbnailSlider">
                <div className="imageViewerFullWidth__thumbnail-slider">
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex === 0}
                            icon="up"
                            onClick={() => {
                                if (activeIndex !== 0) {
                                    mainSwiper?.slidePrev();
                                    thumbSwiper?.update();
                                }
                            }}
                            className="navButton navButtonPrev"
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        className="imageViewerFullWidth-swipercontainer"
                        onSwiper={setThumbSwiper}
                        spaceBetween={10}
                        breakpoints={getEffectiveLayoutSettings(layoutSettings)}
                        watchSlidesProgress={true}
                    >
                        {images &&
                            videos &&
                            images.concat(videos).map((media, index) => (
                                <SwiperSlide
                                    className={classnames(
                                        "imageViewerFullWidth__thumb-slide",
                                        "imageViewerFullWidth-thumbnail",
                                        index === activeIndex &&
                                            "imageViewerFullWidth-activeThumb"
                                    )}
                                    key={`imageViewerFullWidth-thumbs${index}`}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={index > 0}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex + 1 === amount}
                            icon="down"
                            onClick={() => {
                                if (activeIndex + 1 !== amount) {
                                    mainSwiper?.slideNext();
                                    thumbSwiper?.update();
                                }
                            }}
                            className="navButton navButtonNext"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
